import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome, FiSettings, FiUsers, FiMenu,
  FiTruck, FiPackage, FiShoppingCart, FiFileText,
  FiCreditCard, FiShoppingBag, FiBarChart2,
  FiChevronDown, FiChevronRight, FiUser,
  FiDollarSign, FiBook, FiGrid,
  FiTag, FiTool, FiBell, FiHelpCircle,
  FiLogOut, FiArchive, FiArrowDownCircle,
  FiRepeat, FiRefreshCw, FiShield, FiDatabase, FiGlobe, FiSmartphone
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../../styles/Layout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

/* ── Reloj ─────────────────────────────────────────────────────────── */
function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, '0');
  const hh = pad(time.getHours()), mm = pad(time.getMinutes()), ss = pad(time.getSeconds());
  const dd = pad(time.getDate()), mo = pad(time.getMonth() + 1), yy = time.getFullYear();
  return (
    <span className="header-clock">
      {hh}:{mm}:{ss} &nbsp; {dd}/{mo}/{yy}
    </span>
  );
}

/* ── Etiqueta de sección ────────────────────────────────────────────── */
function NavSectionLabel({ label }) {
  return <div className="nav-section-label">{label}</div>;
}

/* ── Grupo colapsable ───────────────────────────────────────────────── */
function NavGroup({ icon, label, children, defaultOpen }) {
  const location = useLocation();
  const anyActive = React.Children.toArray(children).some(
    (c) => c.props?.to && location.pathname.startsWith(c.props.to.split('/').slice(0, 2).join('/'))
  );
  const [open, setOpen] = useState(defaultOpen || anyActive);
  return (
    <div className="nav-group">
      <button
        className={`nav-item nav-group-header ${anyActive ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span className="nav-icon">{icon}</span>
        <span className="nav-label">{label}</span>
        <span className="nav-arrow">{open ? <FiChevronDown /> : <FiChevronRight />}</span>
      </button>
      {open && <div className="nav-sub">{children}</div>}
    </div>
  );
}

/* ── Enlace de navegación ───────────────────────────────────────────── */
function NavLink({ to, label, icon, sub }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Link to={to} className={`nav-item ${sub ? 'nav-sub-item' : ''} ${active ? 'active' : ''}`}>
      {icon && <span className="nav-icon">{icon}</span>}
      <span className="nav-label">{label}</span>
    </Link>
  );
}

/* ── Botón de estado WhatsApp (header) ────────────────────────────────
   Conectado (verde, bloqueado) / Conectar (clickeable, dispara init) /
   Desconectado (rojo suave, bloqueado — nunca se vinculó ningún número). */
function WhatsappStatusButton() {
  const [data, setData] = useState(null); // { status, linked }
  // Se activa al hacer click y NO se apaga hasta que el poll confirme
  // 'connected' (o hasta que `linked` se caiga a false, ej. sesión inválida
  // que el backend termina limpiando solo) — así el botón queda bloqueado
  // todo el tiempo que dure la reconexión real, no solo mientras dura el POST.
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const poll = () => api.get('/api/whatsapp/status')
      .then(res => {
        if (cancelled) return;
        setData(res.data.data);
        // Se desbloquea apenas el estado deje de ser 'initializing' — sin
        // importar a cuál pase. Antes solo se desbloqueaba en 'connected' o
        // '!linked', y si la reconexión pasaba por cualquier otro estado
        // intermedio antes de estabilizarse, el botón quedaba pegado en
        // "En espera..." para siempre aunque ya estuviera conectado.
        if (res.data.data?.status !== 'initializing') setWaiting(false);
      })
      .catch(() => {});
    poll();
    const t = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;

  const handleConectar = async (e) => {
    e.stopPropagation();
    setWaiting(true);
    try {
      const res = await api.post('/api/whatsapp/init');
      setData(res.data.data);
      if (res.data.data?.status !== 'initializing') setWaiting(false);
    } catch (_) {
      setWaiting(false);
    }
  };

  if (data.status === 'connected') {
    return (
      <button className="wa-status-btn wa-status-connected" disabled title="WhatsApp vinculado y listo para enviar">
        <FaWhatsapp size={13} /> Conectado
      </button>
    );
  }

  if (data.linked) {
    return (
      <button className="wa-status-btn wa-status-conectar" onClick={handleConectar} disabled={waiting} title="Reconectar WhatsApp">
        <FaWhatsapp size={13} /> {waiting ? 'En espera...' : 'Conectar'}
      </button>
    );
  }

  return (
    <button className="wa-status-btn wa-status-desconectado" disabled title="Ningún número vinculado — ve a Configuración > WhatsApp">
      <FaWhatsapp size={13} /> Desconectado
    </button>
  );
}

/* ── Botón de estado Backup (header) ──────────────────────────────────
   Bloqueado siempre (solo informa) — Backup Activo (verde) si el último
   backup registrado en el cron del VPS terminó bien y no está vencido,
   Backup Inactivo (rojo) si falló o si no hay uno reciente. */
function BackupStatusButton() {
  const [data, setData] = useState(null); // { available, active, reason, lastSuccessAt }

  useEffect(() => {
    let cancelled = false;
    const poll = () => api.get('/api/backup/status')
      .then(res => { if (!cancelled) setData(res.data.data); })
      .catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data || !data.available) return null;

  const tooltip = data.active
    ? `Backup activo${data.lastSuccessAt ? ' — último: ' + new Date(data.lastSuccessAt).toLocaleString('es-PE') : ''}`
    : (data.reason || 'Backup inactivo');

  return (
    <button
      className={`backup-status-btn ${data.active ? 'backup-status-active' : 'backup-status-inactive'}`}
      disabled
      title={tooltip}
    >
      <FiDatabase size={13} /> {data.active ? 'Backup Activo' : 'Backup Inactivo'}
    </button>
  );
}

/* ── Datos de configuración ─────────────────────────────────────────── */
const CONFIG_ITEMS = [
  { key: 'config-unidades',         to: '/config/unidades',        label: 'Unidad de Medida', icon: <FiTool /> },
  { key: 'config-formas-pago',      to: '/config/formas-pago',     label: 'Forma Pago',       icon: <FiCreditCard /> },
  { key: 'config-tipos-documento',  to: '/config/tipos-documento', label: 'Tipo Documento',   icon: <FiBook /> },
  { key: 'config-docs-pago',        to: '/config/docs-pago',       label: 'Documentos Pago',  icon: <FiFileText /> },
  { key: 'config-series-venta',     to: '/config/series-venta',    label: 'Serie Venta',      icon: <FiTag /> },
  { key: 'config-tipos-movimiento', to: '/config/tipos-movimiento',label: 'Tipo Movimiento',  icon: <FiRepeat /> },
  { key: 'config-whatsapp',         to: '/config/whatsapp',        label: 'WhatsApp',         icon: <FiSmartphone /> },
];

const CAJA_ITEMS = [
  { key: 'caja-apertura',  to: '/caja/cajas',     label: 'Apertura / Cierre' },
  { key: 'caja-historial', to: '/caja/historial', label: 'Historial Cuadres' },
];


/* ── Layout principal ───────────────────────────────────────────────── */
function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [avatarOpen, setAvatarOpen]   = useState(false);
  const { user, logout, isSuperAdmin, menuKeys } = useAuth();
  const navigate = useNavigate();

  // can(key): true si no hay restricción (menuKeys===null) o el key está habilitado
  const can = (key) => !menuKeys || menuKeys.has(key);
  const canAny = (...keys) => keys.some(k => can(k));
  const displayName = user?.full_name || user?.username || '';
  const businessName = isSuperAdmin ? 'SISTEMA — SUPERADMIN' : (user?.business_name || 'TIENDA PRINCIPAL');
  const businessLogo = !isSuperAdmin && user?.business_logo
    ? `${API_URL}${user.business_logo}`
    : null;
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0] || '')
    .join('')
    .toUpperCase();

  useEffect(() => {
    if (isSuperAdmin) {
      document.title = 'Superadmin — DevFox';
    } else if (user?.business_name) {
      document.title = user.business_name;
    }
  }, [user, isSuperAdmin]);

  const handleLogout = () => {
    setAvatarOpen(false);
    logout();
    navigate('/login');
  };

  const visibleCaja     = CAJA_ITEMS.filter(i => can(i.key));
  const visibleConfig   = CONFIG_ITEMS.filter(i => can(i.key));

  return (
    <div className="layout-wrapper" onClick={() => setAvatarOpen(false)}>
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      <header className={`top-header ${isSuperAdmin ? 'header-superadmin' : ''}`}>
        <div className="header-brand">
          {isSuperAdmin ? (
            <div className="brand-devfox">
              <svg viewBox="0 0 36 36" fill="none" className="brand-devfox-icon">
                <path d="M18 2L33 10.5V25.5L18 34L3 25.5V10.5L18 2Z"
                  fill="rgba(14,165,233,0.15)" stroke="url(#dfg)" strokeWidth="1.2"/>
                <rect x="12" y="18" width="12" height="10" rx="1" fill="#0ea5e9" fillOpacity="0.85"/>
                <rect x="14" y="20" width="3" height="2.5" rx="0.4" fill="white" fillOpacity="0.8"/>
                <rect x="19" y="20" width="3" height="2.5" rx="0.4" fill="white" fillOpacity="0.8"/>
                <rect x="14" y="24" width="3" height="4" rx="0.4" fill="white"/>
                <rect x="19" y="24" width="3" height="4" rx="0.4" fill="white" fillOpacity="0.7"/>
                <rect x="12" y="12" width="2" height="5" rx="0.5" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="15.5" y="10" width="2" height="7" rx="0.5" fill="#0ea5e9"/>
                <rect x="19" y="11" width="2" height="6" rx="0.5" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="22.5" y="9" width="2" height="8" rx="0.5" fill="#0284c7"/>
                <defs>
                  <linearGradient id="dfg" x1="3" y1="2" x2="33" y2="34" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#00c6ff"/>
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.5"/>
                  </linearGradient>
                </defs>
              </svg>
              <span className="brand-devfox-text">DevFox</span>
            </div>
          ) : businessLogo ? (
            <img src={businessLogo} alt={businessName} className="brand-logo business-logo" />
          ) : (
            <img src="/logo_pivasa.png" alt="PIVASA" className="brand-logo" />
          )}
        </div>
        <div className="header-center">
          <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <FiMenu />
          </button>
          <span className="header-business-name">{businessName}</span>
          {!isSuperAdmin && <WhatsappStatusButton />}
          {!isSuperAdmin && <BackupStatusButton />}
        </div>
        <div className="header-right">
          <Clock />
          <button className="header-icon-btn" title="Notificaciones"><FiBell size={15} /></button>
          <button className="header-icon-btn" title="Ayuda"><FiHelpCircle size={15} /></button>
          <span className="header-user">{displayName.toUpperCase()}</span>

          {/* Avatar con dropdown */}
          <div
            className="avatar-wrapper"
            onClick={e => { e.stopPropagation(); setAvatarOpen(o => !o); }}
          >
            <div className="header-avatar">{initials || <FiUser size={14} />}</div>

            {avatarOpen && (
              <div className="avatar-dropdown">
                <div className="avatar-dropdown-info">
                  <span className="avatar-dropdown-name">{displayName}</span>
                  <span className="avatar-dropdown-biz">{businessName}</span>
                </div>
                <div className="avatar-dropdown-divider" />
                <button className="avatar-dropdown-item avatar-logout" onClick={handleLogout}>
                  <FiLogOut size={13} />
                  Salir del sistema
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="layout-body">
        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <aside className={`sidebar ${sidebarOpen ? '' : 'closed'}`}>
          <div className={`sidebar-store ${isSuperAdmin ? 'sidebar-store-superadmin' : ''}`}>
            {isSuperAdmin ? 'SUPERADMIN' : 'TIENDA PRINCIPAL'}
          </div>

          <nav className="sidebar-nav">

            {/* Superadmin: panel de empresas */}
            {isSuperAdmin && (
              <>
                <NavSectionLabel label="SUPERADMIN" />
                <NavLink to="/empresas" label="Gestión Empresas" icon={<FiGlobe />} />
              </>
            )}

            {/* ── Menú de empresa (oculto para superadmin) ── */}
            {!isSuperAdmin && (
              <>
                {can('inicio') && <NavLink to="/" label="Inicio" icon={<FiHome />} />}

                {canAny('clientes', 'proveedores', 'familia-productos', 'almacenes', 'productos') && (
                  <>
                    <NavSectionLabel label="MAESTROS" />
                    {can('clientes')          && <NavLink to="/clientes"         label="Clientes"          icon={<FiUsers />} />}
                    {can('proveedores')       && <NavLink to="/proveedores"       label="Proveedores"       icon={<FiTruck />} />}
                    {can('familia-productos') && <NavLink to="/familia-productos" label="Familia Productos" icon={<FiGrid />} />}
                    {can('almacenes')         && <NavLink to="/almacenes"         label="Almacenes"         icon={<FiArchive />} />}
                    {can('productos')         && <NavLink to="/productos"         label="Productos"         icon={<FiPackage />} />}
                  </>
                )}

                {canAny('movimientos', 'transferencias', 'conversiones', 'stocks') && (
                  <>
                    <NavSectionLabel label="ALMACENES" />
                    {can('movimientos')    && <NavLink to="/movimientos"                label="Movimientos"    icon={<FiRepeat />} />}
                    {can('transferencias') && <NavLink to="/inventarios/transferencias" label="Transferencias" icon={<FiArrowDownCircle />} />}
                    {can('conversiones')   && <NavLink to="/inventarios/conversiones"   label="Conversiones"   icon={<FiRefreshCw />} />}
                    {can('stocks')         && <NavLink to="/inventarios/stocks"         label="Stocks"         icon={<FiDatabase />} />}
                  </>
                )}

                {canAny('compras', 'cuentas-pagar') && (
                  <>
                    <NavSectionLabel label="COMPRAS" />
                    {can('compras')       && <NavLink to="/compras"       label="Compras"           icon={<FiShoppingBag />} />}
                    {can('cuentas-pagar') && <NavLink to="/cuentas-pagar" label="Cuentas por Pagar" icon={<FiCreditCard />} />}
                  </>
                )}

                {canAny('ventas', 'cuentas-cobrar', 'cobranzas', ...CAJA_ITEMS.map(i => i.key)) && (
                  <>
                    <NavSectionLabel label="VENTAS" />
                    {can('ventas')         && <NavLink to="/ventas"         label="Ventas"             icon={<FiShoppingCart />} />}
                    {can('cuentas-cobrar') && <NavLink to="/cuentas-cobrar" label="Cuentas por Cobrar" icon={<FiFileText />} />}
                    {can('cobranzas')      && <NavLink to="/cobranzas"      label="Cobranzas"          icon={<FiArrowDownCircle />} />}
                    {visibleCaja.length > 0 && (
                      <NavGroup icon={<FiDollarSign />} label="Caja">
                        {visibleCaja.map(item => (
                          <NavLink key={item.to} to={item.to} label={item.label} sub />
                        ))}
                      </NavGroup>
                    )}
                  </>
                )}

                {can('reportes') && (
                  <>
                    <NavSectionLabel label="REPORTES" />
                    <NavLink to="/reportes" label="Reportes" icon={<FiBarChart2 />} />
                  </>
                )}

                {visibleConfig.length > 0 && (
                  <>
                    <NavSectionLabel label="CONFIGURACION" />
                    <NavGroup icon={<FiSettings />} label="Configuracion">
                      {visibleConfig.map(item => (
                        <NavLink key={item.to} to={item.to} label={item.label} icon={item.icon} sub />
                      ))}
                    </NavGroup>
                  </>
                )}

                {canAny('usuarios', 'roles') && <NavSectionLabel label="SEGURIDAD" />}
                {can('usuarios') && <NavLink to="/usuarios" label="Usuarios" icon={<FiUsers />} />}
                {can('roles')    && <NavLink to="/roles"    label="Roles"    icon={<FiShield />} />}
              </>
            )}

          </nav>

        </aside>

        {/* ── CONTENIDO ───────────────────────────────────────────── */}
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
